import { scaleSequential } from 'd3-scale';
import { interpolateViridis, interpolateRdYlGn } from 'd3-scale-chromatic';

/* WLI as a frequency of days.
 *
 * WLI is a reading's mid-rank percentile within its own gauge's record, pushed through the
 * normal quantile function, so the map from index to frequency is exact and symmetric:
 *
 *     z = 3 * WLI / 100      p = Phi(z)      days between days this extreme = 1 / (1 - p)
 *
 * Recomputed 2026-08-31 (design/29 SS1); these are the anchors, not approximations:
 *
 *     1 day per week    +-35.6      1 day per month   +-61.3      1 day per year   +-92.6
 *
 * The published scale ends at +-100, which is the 0.135th / 99.865th percentile: 1 day in
 * 741, about once every 2.03 years. That is the rarest thing this bar can say. "1 per 5
 * years" is WLI 108.8 and "1 per 10 years" is 115.2 -- both off the end by construction, so
 * the ends are described as ~2 years and the scale is not stretched to reach them.
 *
 * These are frequencies of DAYS, not hydrological return periods. "1/yr" means the level is
 * exceeded on roughly one day a year, NOT that it is a 1-in-1-year flood: return periods come
 * from annual maxima, and water level is strongly autocorrelated, so a single flood
 * contributes many exceedance-days. design/29 SS3, and the external white paper, hold the
 * same line -- keep the wording here as day frequency.
 *
 * The map colours an area by max_abs_anomaly -- its single most extreme gauge, not its mean --
 * so the scale is a statement about ONE gauge. An area with forty gauges reaches the
 * once-a-year mark far more often than once a year. That qualifier is carried visibly in
 * scaleCaption and in full in scaleCaptionTitle (design/29 SS4, option 2).
 */
const WLI_FREQUENCY_TICKS = [
  { wli: -92.6, label: '1/yr', title: 'About one day a year is lower than this' },
  { wli: -61.3, label: '1/mo', title: 'About one day a month is lower than this' },
  { wli: -35.6, label: '1/wk', title: 'About one day a week is lower than this' },
  { wli: 35.6, label: '1/wk', title: 'About one day a week is higher than this' },
  { wli: 61.3, label: '1/mo', title: 'About one day a month is higher than this' },
  { wli: 92.6, label: '1/yr', title: 'About one day a year is higher than this' }
].map((tick) => ({ ...tick, position: (tick.wli + 100) / 200 }));

const WLI_SCALE_EXPLAINER = 'The Water Level Index is a reading\'s percentile within its own '
  + 'gauge\'s record, so these labels are frequencies of days at a single gauge -- "1/yr" means '
  + 'about one day a year is this extreme. They are not flood return periods. An area is '
  + 'coloured by its most extreme gauge, so an area reaching 1/yr means one of its gauges did.';

export const ATTRIBUTE_OPTIONS = {
  wli: {
    label: 'Water Level Index',
    rangeTitle: 'WLI',
    rangeValues: ['-100', '+100'],
    rangeLabels: ['Dry', 'Wet'],
    scaleTicks: WLI_FREQUENCY_TICKS,
    scaleCaption: [
      'How often one gauge has a day this extreme.',
      'The ends, ±100, are about once in 2 years.'
    ],
    scaleCaptionTitle: WLI_SCALE_EXPLAINER,
    colorMapInterpolator: scaleSequential(interpolateViridis).domain([1, 0])
  },
  gaugeCount: {
    // Deliberately no scaleTicks/scaleCaption: gauge concentration is a count, and the
    // frequency reading of the bar does not transfer to it.
    label: 'Data Scarcity',
    rangeTitle: 'Gauge Concentration',
    rangeValues: ['', ''],
    rangeLabels: ['Few', 'Many'],
    colorMapInterpolator: interpolateRdYlGn
  }
};

export const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
  function toRad(x) {
    return (x * Math.PI) / 180;
  }

  const R = 6371; // Radius of the Earth in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
      * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
};

export const formatDateString = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

export const API_GATEWAY_URL = 'https://f0nfrr51sk.execute-api.us-west-2.amazonaws.com';

export const NEARBY_GAUGES_RADIUS = 30;

export const IS_IPAD = navigator.maxTouchPoints && navigator.maxTouchPoints > 2;

export const H3_HEXAGON_RES = 4;

export const IS_PRODUCTION = process.env.NODE_ENV !== 'development';

/* ------------------------------------------------------------------ view modes
 * The map draws one row per area per date. That contract is identical for H3 and for
 * administrative areas -- only the geometry and the key change -- so a view mode is a
 * choice of (values URL, geometry source), not a second application.
 *
 * `adm` geometry is fetched as one GeoJSON file and cached separately from the values,
 * because it changes when the boundary set changes and the values change every day.
 * See ADM-SHAPES.md for the payload measurements behind that split.
 */
export const VIEW_MODES = {
  adm: {
    key: 'adm',
    label: 'Regions',
    // semantic_level 2: county in the USA, *Kreis* in Germany, departement in France.
    // NEVER the source ADM level -- source_level 2 in Germany is the Regierungsbezirk,
    // a tier several Laender have abolished, and misses all 428 Kreise silently.
    areaNoun: 'region',
    jsonURL: process.env.REACT_APP_ADM_JSON_URL || 'https://wli.divirod.com/wli_admin.json',
    shapesURL: process.env.REACT_APP_ADM_SHAPES_URL || 'https://wli.divirod.com/adm2_shapes.json'
  },
  h3: {
    key: 'h3',
    label: 'Hexagons',
    areaNoun: 'hexagon',
    jsonURL: process.env.REACT_APP_JSON_URL || 'https://wli.divirod.com/wli.json',
    shapesURL: null
  }
};

// Which view the map opens on. H3 stays reachable either way -- the ADM path is additive.
/* `adm` is the landing view as of 2026-09-01, on Heiko's decision. Hexagons remain one
 * click away on the Regions/Hexagons switch and their data is refreshed by the same job.
 *
 * The reason for the change is what the two views actually say. An H3 res-4 cell is ~1,770
 * km2 of nothing in particular; a county or a *Kreis* is a place a reader already knows, can
 * search for, and can act on. The hexagon view was never a choice about legibility -- it was
 * what the pipeline could produce before `admin_day` existed.
 *
 * `REACT_APP_VIEW_MODE` still overrides, which is how the local ADM build and any future
 * regional variant pin their own default without touching this line.
 */
export const DEFAULT_VIEW_MODE = (
  VIEW_MODES[process.env.REACT_APP_VIEW_MODE] ? process.env.REACT_APP_VIEW_MODE : 'adm'
);

/* --------------------------------------------------------- point in polygon
 * The geo-search hands back a lat/lng and the map has to say which area contains it.
 * In H3 that is `latLngToCell`, a pure function. There is no such function for an
 * arbitrary boundary set, so this is a bbox prefilter and then ray casting.
 *
 * Bboxes are computed once when the shapes load; without the prefilter this walks
 * several million coordinates per search.
 */
const ringContains = (ring, x, y) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    // A vertex exactly on the ray is counted once, by testing the two ends asymmetrically.
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

// A polygon is [outerRing, ...holes]. A point in a hole is not in the polygon.
const polygonContains = (rings, x, y) => {
  if (!rings.length || !ringContains(rings[0], x, y)) return false;
  for (let i = 1; i < rings.length; i += 1) {
    if (ringContains(rings[i], x, y)) return false;
  }
  return true;
};

export const computeBoundingBox = (geometry) => {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const visit = (node) => {
    if (Array.isArray(node) && typeof node[0] === 'number') {
      const [x, y] = node;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      return;
    }
    if (Array.isArray(node)) node.forEach(visit);
  };
  visit(geometry.coordinates);
  return [minX, minY, maxX, maxY];
};

export const findAreaAtPoint = (features, lng, lat) => {
  if (!features) return undefined;
  for (let i = 0; i < features.length; i += 1) {
    const feature = features[i];
    const bbox = feature.bbox || computeBoundingBox(feature.geometry);
    if (lng >= bbox[0] && lng <= bbox[2] && lat >= bbox[1] && lat <= bbox[3]) {
      const { type, coordinates } = feature.geometry;
      const hit = type === 'Polygon'
        ? polygonContains(coordinates, lng, lat)
        : coordinates.some((polygon) => polygonContains(polygon, lng, lat));
      if (hit) return feature;
    }
  }
  return undefined;
};
